// MarginWarningModal — Phase 9b 80%-threshold soft warning.
//
// Fires when /admin/auth/me returns marginStatus === 'warning'
// (-wallet ≥ 0.8·deposit but < deposit). Dismissible, persistent.
//
// Phase 10 follow-up (2026-05-12) — clarity overhaul.
//
// The original warning modal was a small centred card with a brief
// "why this is showing" blurb and the recharge form bolted underneath.
// Compared to the Bailout Portal (the locked-state full-page gate),
// it felt cryptic: the agent didn't understand WHY it was firing or
// WHAT would happen if they ignored it. This rewrite mirrors the
// Bailout Portal's storytelling beat-for-beat — same crisis header
// style (amber instead of rose), same plain-language "why this
// happened" panel, a headroom-to-lockout gauge (mirror of the
// recovery progress bar), an "if you ignore" panel that lists what
// breaks when they cross 100%, and finally the three-tier picker
// (already part of EmergencyRechargeForm) for paying it down.
//
// Recurrence cadence (unchanged):
//   1. Fires on every ShellRoute mount (the dominant trigger).
//   2. setInterval re-arms every 10 minutes if the agent stays put.
//   3. After dismiss, a 60-second grace window suppresses the next
//      mount/timer fire so dismiss + nav-click in one gesture doesn't
//      re-pop instantly.
//
// Z-9000 — sits BELOW EmergencyLockoutGate (z-9999) so a state flip
// to 'locked' takes over cleanly, and BELOW AgentNavBar (z-10000)
// so the top bar's wallet chip, bell, and theme toggle stay tappable
// even when the warning is showing.
//
// Top padding leaves space for the bar height + safe-area inset on
// notched phones — bar visually sits above the warning card with a
// small gutter.
//
// Dismissibility: X button, ESC key, backdrop click — all close. A
// successful submit auto-closes via onSubmittedSuccess.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';
import EmergencyRechargeForm from './EmergencyRechargeForm';

const RECURRENCE_INTERVAL_MS = 10 * 60 * 1000;   // 10 minutes
const POST_DISMISS_GRACE_MS  = 60 * 1000;        // 60 seconds

// Module-scope so dismissal grace persists across remounts (which
// happen on every navigation). Set to a Date.now() timestamp at
// dismiss; reads use Date.now() - lastDismissedAt < GRACE_MS.
let lastDismissedAt = 0;

const inr        = (n: number) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const inrSigned  = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${inr(Math.abs(n || 0))}`;

export default function MarginWarningModal() {
  const { admin } = useAdminAuth();
  const t = useT();

  // Open is the single source of truth for whether the modal is
  // currently visible. Defaults to !inGrace, so re-mounts after the
  // grace window has elapsed pop the modal again.
  const [open, setOpen] = useState(() => {
    return Date.now() - lastDismissedAt >= POST_DISMISS_GRACE_MS;
  });

  // 10-minute timer — re-arms while the modal is closed AND we've
  // exited the grace window. Cleared on unmount or when the modal
  // opens (reset starts when it closes again).
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (open) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    timerRef.current = setTimeout(() => {
      if (Date.now() - lastDismissedAt >= POST_DISMISS_GRACE_MS) {
        setOpen(true);
      }
    }, RECURRENCE_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open]);

  // ESC closes (different from the lockout — this one IS dismissible).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Body scroll lock while open — same as lockout, but reversible.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const dismiss = useCallback(() => {
    lastDismissedAt = Date.now();
    setOpen(false);
  }, []);

  const onSubmittedSuccess = useCallback(() => {
    setOpen(false);
  }, []);

  // Live balances from /auth/me payload — same source the lockout
  // gate reads, so the two modals always agree on what they show.
  const wallet    = Number((admin as any)?.walletBalance)  | 0;
  const deposit   = Number((admin as any)?.depositBalance) | 0;
  const negWallet = wallet < 0 ? -wallet : 0;
  const sum       = wallet + deposit;

  // Headroom-to-lockout gauge. The locked state hits when sum ≤ 0.
  // Warning starts when sum ≤ 20% of deposit AND sum > 0. So the
  // "danger zone" the agent is moving through during warning runs
  // from 20%-of-deposit down to 0 — and the bar should show how
  // close they are to lockout (0% = just entered warning, 100% =
  // about to lock).
  //
  // Mirror of the lockout's recovery bar: TRACK is amber (current
  // crisis), FILL is the rose "danger zone consumed" growing from
  // the LEFT. At 0% the bar is fully amber → safer. At 100% it's
  // fully rose → about to lock. "More red = more danger" reads
  // intuitively for the warning state.
  const lockoutPct = useMemo(() => {
    if (deposit <= 0) return 100;
    const dangerEntry = Math.ceil(0.2 * deposit);   // sum where warning fires
    const lockEntry   = 0;                          // sum where lockout fires
    if (dangerEntry <= lockEntry) return 100;
    // How far the agent has descended from dangerEntry → lockEntry.
    // sum currently in (lockEntry, dangerEntry]. Clamp to that band.
    const clamped = Math.max(lockEntry, Math.min(dangerEntry, sum));
    const pct = ((dangerEntry - clamped) / (dangerEntry - lockEntry)) * 100;
    return Math.max(0, Math.min(100, pct));
  }, [deposit, sum]);

  // The headroom number: how much more LOSS the agent's wallet can
  // absorb before lockout fires. Equal to `sum` (since lockout is at
  // sum ≤ 0). Always ≥ 1 in warning (or we'd already be locked) — so
  // we can show it without worrying about negative values.
  const headroomToLockout = Math.max(0, sum);

  if (!open) return null;

  return (
    <div
      // z-9000 sits below the lockout gate (z-9999) so a state flip
      // to 'locked' takes over cleanly, AND below AgentNavBar
      // (z-10000) so the top bar stays tappable.
      //
      // pt-* reserves space for the persistent top bar. Bar height
      // is ~3.5rem mobile / ~4rem desktop, plus its own ~0.75rem top
      // padding, plus env(safe-area-inset-top) on notched phones.
      // 5rem (mobile) / 5.5rem (desktop) covers all three plus a
      // small gutter so the bar visually sits above the warning card.
      className="fixed inset-0 z-[9000] flex flex-col items-center
                 bg-black/70 backdrop-blur-sm overflow-y-auto overscroll-contain
                 pl-[calc(0.5rem+env(safe-area-inset-left))]
                 pr-[calc(0.5rem+env(safe-area-inset-right))]
                 pt-[calc(env(safe-area-inset-top)+5rem)]
                 pb-[calc(2rem+env(safe-area-inset-bottom))]
                 sm:pl-[calc(1rem+env(safe-area-inset-left))]
                 sm:pr-[calc(1rem+env(safe-area-inset-right))]
                 sm:pt-[calc(env(safe-area-inset-top)+5.5rem)]"
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
      aria-labelledby="margin-warning-title"
    >
      {/* shrink-0 keeps the card from collapsing to viewport height —
          same fix as the lockout gate, so the wrapper's overflow-y-auto
          engages and the user can scroll on any screen size. */}
      <div
        className="w-full max-w-2xl shrink-0 rounded-3xl
                   bg-white dark:bg-arena-900
                   border-2 border-amber-300 dark:border-amber-500/50
                   shadow-[0_20px_60px_rgba(245,158,11,0.45)]
                   overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >

        {/* ─── 1. CRISIS HEADER ─────────────────────────────────────
            Mirror of the lockout's rose gradient header — same big
            type, same tracking-[0.22em] eyebrow, same supporting
            paragraph. Tone flips to amber so the agent immediately
            reads "serious but not catastrophic". */}
        <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 px-5 sm:px-7 py-6 text-white">
          <div aria-hidden className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/15 blur-3xl" />
          <div aria-hidden className="absolute -bottom-20 -left-20 w-48 h-48 rounded-full bg-yellow-300/20 blur-3xl" />
          <div className="relative flex items-start gap-3 pr-12">
            <span className="grid place-items-center w-14 h-14 rounded-2xl
                             bg-white/15 ring-2 ring-white/30 shrink-0
                             shadow-[0_4px_18px_rgba(0,0,0,0.18)]">
              <Icon name="warning" size={32} filled />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] font-bold tracking-[0.22em] uppercase opacity-90">
                {t('agent.warning.header.eyebrow')}
              </div>
              <h1 id="margin-warning-title"
                  className="text-2xl sm:text-[28px] font-black tracking-tight leading-tight mt-1">
                {t('agent.warning.header.title')}
              </h1>
              <p className="text-[13px] sm:text-[13.5px] text-amber-50 mt-2 leading-snug max-w-prose">
                {t('agent.warning.header.body')}
              </p>
            </div>
          </div>
          {/* X close — top-right corner, same crisis-overlay layout
              the lockout uses for its lock icon, but the warning IS
              dismissible so we surface a close affordance. */}
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('agent.warning.header.dismissAria')}
            className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-full
                       bg-white/15 hover:bg-white/25 transition active:scale-95"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* ─── 2. WHY THIS IS SHOWING ──────────────────────────────
            Plain-English storytelling, identical layout to the
            lockout's "Why this happened" panel — rounded icon tile
            + section eyebrow + two paragraphs. First paragraph
            states the cause; second paragraph reassures the agent
            that the platform is alerting them EARLY by design, so
            they're not punished for hitting the threshold. */}
        <div className="px-5 sm:px-7 py-4 sm:py-5 bg-arena-50/60 dark:bg-arena-700/30">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0
                             bg-amber-100 text-amber-700
                             dark:bg-amber-500/15 dark:text-amber-300
                             ring-1 ring-amber-200 dark:ring-amber-400/30">
              <Icon name="info" size={18} filled />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-700 dark:text-neon-200/85 mb-0.5">
                {t('agent.warning.why.eyebrow')}
              </div>
              <p className="text-[13px] text-arena-900 dark:text-white leading-snug">
                {t('agent.warning.why.body', { deposit: inr(deposit) })}
              </p>
              <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mt-1.5 leading-snug">
                {t('agent.warning.why.reassurance')}
              </p>
            </div>
          </div>
        </div>

        {/* ─── 3. HEADROOM-TO-LOCKOUT GAUGE ─────────────────────────
            Mirror of the lockout's recovery gauge — same big number
            up top, same percentage badge on the right, same
            two-column wallet+deposit breakdown below.

            Semantics flip: lockout shows progress TOWARD safety
            (green fill grows); warning shows danger TOWARD lockout
            (red fill grows from the left). "More red = closer to
            lockout" reads instantly. */}
        <div className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50">
          <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/75">
                {t('agent.warning.gauge.label')}
              </div>
              <div className="font-display font-black text-3xl sm:text-4xl tabular-nums text-amber-700 dark:text-amber-300 leading-none mt-1">
                {inr(headroomToLockout)}
                <span className="ml-2 text-[12px] font-bold uppercase tracking-wider text-arena-600 dark:text-neon-200/75">
                  {t('agent.warning.gauge.suffix')}
                </span>
              </div>
            </div>
            <span className="text-[11px] font-mono tabular-nums font-extrabold text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20">
              {t('agent.warning.gauge.dangerBadge', { pct: lockoutPct.toFixed(0) })}
            </span>
          </div>

          {/* Track = amber (current warning state). Fill = rose
              (descent toward lockout), growing from the LEFT. At 0%
              the bar is fully amber (just crossed 80% threshold).
              At 100% it's fully rose (about to lock). */}
          <div className="relative h-3.5 rounded-full overflow-hidden
                          bg-amber-200 dark:bg-amber-500/35
                          ring-1 ring-amber-300/60 dark:ring-amber-400/40">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out
                         bg-gradient-to-r from-rose-500 to-rose-600
                         shadow-[0_0_10px_rgba(244,63,94,0.55)]"
              style={{ width: `${lockoutPct}%` }}
            />
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
            <div>
              <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/70">{t('agent.warning.gauge.field.wallet')}</div>
              <div className={`text-[14px] font-bold tabular-nums ${wallet < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-arena-900 dark:text-white'}`}>
                {inrSigned(wallet)}
              </div>
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/70">{t('agent.warning.gauge.field.deposit')}</div>
              <div className="text-[14px] font-bold tabular-nums text-arena-900 dark:text-white">
                {inr(deposit)}
              </div>
            </div>
          </div>
        </div>

        {/* ─── 4. IF YOU IGNORE THIS ────────────────────────────────
            Concrete list of what the agent loses if they don't act
            and cross 100%. Same "CAN'T (temporarily)" layout the
            lockout uses for its CAN/CANNOT lists — same rose icon
            pip, same line-by-line presentation. Removes the "ah it's
            just a warning, I'll ignore it" temptation by being
            specific. */}
        <div className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/75 mb-2.5">
            {t('agent.warning.ignore.eyebrow')}
          </div>
          <div className="rounded-2xl border p-3.5
                          bg-rose-50/60 dark:bg-rose-500/[0.08]
                          border-rose-200/60 dark:border-rose-400/30">
            <div className="text-[11px] uppercase tracking-wider font-extrabold mb-2 text-rose-800 dark:text-rose-200">
              {t('agent.warning.ignore.cardTitle')}
            </div>
            <ul className="space-y-1.5">
              {[
                'agent.warning.ignore.item.dashboard',
                'agent.warning.ignore.item.pages',
                'agent.warning.ignore.item.manualTopup',
                'agent.warning.ignore.item.newWithdrawal',
                'agent.warning.ignore.item.subAgents',
                'agent.warning.ignore.item.profile',
              ].map((k) => (
                <li key={k} className="flex items-start gap-2 text-[12.5px] leading-snug">
                  <span className="grid place-items-center w-4 h-4 rounded-full shrink-0 mt-0.5 bg-rose-500 text-white">
                    <Icon name="close" size={11} filled />
                  </span>
                  <span className="text-arena-900 dark:text-white">{t(k)}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-rose-700 dark:text-rose-200/85 mt-2.5 leading-snug">
              {t('agent.warning.ignore.footer')}
            </p>
          </div>
        </div>

        {/* ─── 5. RECOVER NOW (form + three-tier picker) ────────────
            The EmergencyRechargeForm already owns the three-tier
            picker + parent-payment QR + UTR submission. Render it in
            warning severity so it uses the amber-themed picker and
            defaults the amount to safeMinimum (the real-cushion
            tier, not the "just barely escape" one). */}
        <div className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/75 mb-2.5">
            {t('agent.warning.recoverEyebrow')}
          </div>
          <EmergencyRechargeForm
            severity="warning"
            onSubmittedSuccess={onSubmittedSuccess}
          />
        </div>

        {/* ─── 6. DISMISS FOOTER ────────────────────────────────────
            Always-available "I'll deal with this later" — the
            warning is dismissible, unlike the lockout. The label
            is plain so the agent doesn't feel pressured into
            paying right now if they want to think about it. */}
        <div className="px-5 sm:px-7 py-5 border-t border-arena-200/60 dark:border-arena-600/50
                        bg-amber-50/60 dark:bg-amber-500/[0.06]">
          <button
            type="button"
            onClick={dismiss}
            className="w-full inline-flex items-center justify-center gap-1.5
                       rounded-full px-4 py-2.5 text-[12.5px] font-semibold transition
                       text-arena-700 hover:text-arena-900 dark:text-neon-200/80 dark:hover:text-white
                       bg-white/60 dark:bg-arena-700/40 hover:bg-white dark:hover:bg-arena-600/55
                       border border-arena-200/60 dark:border-arena-600/50"
          >
            <Icon name="schedule" size={14} />
            {t('agent.warning.dismiss.cta')}
          </button>
          <p className="text-center text-[11px] text-arena-600 dark:text-neon-200/65 mt-2 leading-snug">
            {t('agent.warning.dismiss.footnote')}
          </p>
        </div>

      </div>
    </div>
  );
}
